import React, { useEffect, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { getToken } from "@/lib/auth";
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "../../styles/heatmap.css";

const ACTIVITY_BUCKETS = [
  { key: "hot",    label: "Hot",    color: "#eab308", intensity: 0.90 },
  { key: "strong", label: "Strong", color: "#f97316", intensity: 0.65 },
  { key: "active", label: "Active", color: "#14b8a6", intensity: 0.42 },
  { key: "low",    label: "Low",    color: "#3b82f6", intensity: 0.15 },
];

function getVisibleRankBucket(idx, total) {
  if (total <= 1) return ACTIVITY_BUCKETS[0];
  const pct = idx / (total - 1);
  if (pct < 0.25) return ACTIVITY_BUCKETS[0];
  if (pct < 0.50) return ACTIVITY_BUCKETS[1];
  if (pct < 0.75) return ACTIVITY_BUCKETS[2];
  return ACTIVITY_BUCKETS[3];
}

function getActivityStyle(bucket, idx) {
  return {
    radius: idx === 0 ? 22 : 10 + Math.pow(bucket.intensity, 1.2) * 18,
    fillColor: bucket.color,
    color: bucket.color,
    weight: 0,
    opacity: 1,
    fillOpacity: 0.86,
    className:
      bucket.key === "hot"
        ? "heat-glow-strong"
        : bucket.key === "strong"
        ? "heat-glow-soft"
        : ""
  };
}

function ZoomableMarker({ center, radius, pathOptions, className, children }) {
  const map = useMap();
  return (
    <CircleMarker
      center={center}
      radius={radius}
      pathOptions={pathOptions}
      className={className}
      eventHandlers={{ click: () => map.flyTo(center, 9, { duration: 0.8 }) }}
    >
      {children}
    </CircleMarker>
  );
}

export default function AdminHeatmap() {
  const [data, setData]       = useState(null);
  const [eventFilter, setEventFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  useEffect(() => {
    const token = getToken();
    setLoading(true);
    fetch(
      eventFilter === "all"
        ? "/api/admin/analytics/heatmap"
        : `/api/admin/analytics/heatmap?event=${eventFilter}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setError("Failed to load heatmap: " + e); setLoading(false); });
  }, [eventFilter]);

  const zones   = data?.zones   || [];
  const summary = data?.summary || {};

  // Sort descending by event count so idx === rank
  const sortedZones = [...zones].sort(
    (a, b) => (b.event_count ?? b.user_count ?? 0) - (a.event_count ?? a.user_count ?? 0)
  );

  const visibleMapZones   = sortedZones.filter((z) => z.lat && z.lng).slice(0, 8);
  const visibleTableZones = sortedZones.slice(0, 20);

  return (
    <AdminShell>
      <div className="p-6 md:p-10 text-white space-y-6">

        {/* HEADER */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-3xl font-display">User Location Heatmap</h1>
          <select
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
            className="bg-white/5 border border-white/10 text-white text-sm rounded-xl px-4 py-2 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
          >
            {["all","home_view","guide_view","guide_cta_clicked","cta_start_clicked",
              "onboarding_start","login_clicked","logout_clicked"].map((e) => (
              <option key={e} value={e} className="bg-[#0d1117]">{e}</option>
            ))}
          </select>
        </div>

        {/* METRICS */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Card label="Active Zones"  value={summary.active_zones  || 0} />
          <Card label="Total Events"  value={summary.total_events  || 0} />
          <Card label="Event Filter"  value={summary.event_filter  || eventFilter} />
        </div>

        {loading && <div className="text-white/40 py-10">Loading map…</div>}
        {error   && <div className="text-red-400 text-sm py-4">{error}</div>}

        {!loading && !error && (
          <>
            {/* MAP */}
            <div className="h-[520px] rounded-xl overflow-hidden border border-white/10">
              <MapContainer center={[37.8, -96]} zoom={4} style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                {visibleMapZones.map((z, idx) => {
                  const bucket = getVisibleRankBucket(idx, visibleMapZones.length);
                  const style  = getActivityStyle(bucket, idx);
                  const count  = z.event_count ?? z.user_count ?? 0;
                  const zkey   = z.zip || `${z.city}-${z.state}`;
                  return (
                    <ZoomableMarker
                      key={`${zkey}-${idx}`}
                      center={[Number(z.lat), Number(z.lng)]}
                      radius={style.radius}
                      pathOptions={{
                        fillColor:   style.fillColor,
                        color:       style.color,
                        weight:      style.weight,
                        opacity:     style.opacity,
                        fillOpacity: style.fillOpacity,
                      }}
                      className={style.className}
                    >
                      <Tooltip direction="top" offset={[0, -6]} opacity={1}>
                        <div className="heat-tooltip">
                          <div className="heat-title">{z.city}, {z.state}</div>
                          <div className="heat-meta">{count} events</div>
                          <div className={`heat-badge heat-${bucket.key}`}>
                            {bucket.label}
                          </div>
                        </div>
                      </Tooltip>

                    </ZoomableMarker>
                  );
                })}
              </MapContainer>
            </div>

            {/* LEGEND */}
            <div className="flex flex-wrap items-center gap-5 text-xs text-white/60">
              <span className="font-medium text-white/80">Legend:</span>
              {[...ACTIVITY_BUCKETS].reverse().map((b) => (
                <span key={b.key} className="flex items-center gap-1.5">
                  <span
                    className="h-3 w-3 rounded-full inline-block"
                    style={{ background: b.color, boxShadow: `0 0 6px ${b.color}66` }}
                  />
                  {b.label}
                </span>
              ))}
            </div>

            {/* TABLE */}
            <div className="space-y-2">
              {visibleTableZones.map((z, idx) => {
                const bucket = getVisibleRankBucket(idx, visibleTableZones.length);
                const count  = z.event_count ?? z.user_count ?? 0;
                const zkey   = z.zip || `${z.city}-${z.state}`;
                return (
                  <div key={z.zip || zkey} className="glass p-3 rounded-xl flex justify-between items-center">
                    <span>{z.zip} — {z.city}, {z.state}</span>
                    <span>
                      <span className="text-white/60">{count}</span>
                      {" · "}
                      <span style={{ color: bucket.color }}>{bucket.label}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}

      </div>
    </AdminShell>
  );
}

function Card({ label, value }) {
  return (
    <div className="glass p-4 rounded-xl">
      <div className="text-white/50 text-sm">{label}</div>
      <div className="text-xl font-bold">{value}</div>
    </div>
  );
}
